import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { orgUrl } from '../core/org-url';
import { FirebaseService } from '../core/services/firebase.service';
import { OrgContextService } from '../core/services/org-context.service';

/**
 * `/o/:orgId/...`: the URL decides the organization of this tab. The org must
 * be one the user belongs to (the impersonation principal is a temporary
 * member of its client org, so it qualifies); anything else lands on the
 * "no access" page — never silently another org.
 */
export const orgFromUrlGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const orgContext = inject(OrgContextService);
  const orgId = route.paramMap.get('orgId');
  if (!orgId) return router.parseUrl('/');

  const outcome = await orgContext.activateOrg(orgId);
  switch (outcome) {
    case 'ok':
      return true;
    case 'reload':
      // Another org is active in this tab (in-app link / back-forward into a
      // different org): load the URL fresh so nothing of the previous org's
      // state survives.
      window.location.assign(state.url);
      return false;
    case 'unavailable':
      return router.parseUrl('/region-unavailable');
    case 'no-access':
      return router.createUrlTree(['/no-access'], { queryParams: { org: orgId } });
  }
};

/**
 * Legacy URLs without an org (`/billing`, `/home`, bookmarks, links from other
 * apps): resolve an org — the one active in this tab, else the per-device
 * hint, else the legacy currentOrganizationId, else the first org — and
 * redirect to the same page under `/o/{orgId}`.
 */
export const legacyOrgRedirect: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const orgContext = inject(OrgContextService);
  // Data residency: init() pinned the hinted org as unreachable → say so rather
  // than landing in another org.
  if (!orgContext.currentOrg() && inject(FirebaseService).regionError()) {
    return router.parseUrl('/region-unavailable');
  }
  const orgId = orgContext.defaultOrgId();
  return router.parseUrl(orgId ? orgUrl(orgId, state.url) : '/new');
};
